import Papa from 'papaparse';

const HEADER_ROW_NUMBER = 1;
const MAX_ROWS = 500;
const MAX_FIELD_LENGTH = 255;

const MANDATORY_COLUMN_TO_FIELD = {
  'EPR ID': 'eprId',
  'EPR System': 'eprSystem',
  'Patient Family Name': 'familyName',
  'Patient Given Name': 'givenName',
} as const;

const OPTIONAL_COLUMN_TO_FIELD = {
  'Care Team': 'careTeam',
  'Healthcare System ID': 'healthCareSystemId',
  'Patient Phone Number': 'patientPhoneNumber',
  'Postal Code': 'postalCode',
} as const;

const CONTACT_COLUMN_TO_FIELD = {
  carer: {
    'Carer Family Name': 'familyName',
    'Carer Given Name': 'givenName',
    'Carer Phone Number': 'phoneNumber',
  },
  informant1: {
    'Informant1 Family Name': 'familyName',
    'Informant1 Given Name': 'givenName',
    'Informant1 Phone Number': 'phoneNumber',
  },
} as const;

type ContactRespondentType = keyof typeof CONTACT_COLUMN_TO_FIELD;

const CONTACT_COLUMN_GROUPS = Object.entries(CONTACT_COLUMN_TO_FIELD) as [
  ContactRespondentType,
  Record<string, 'familyName' | 'givenName' | 'phoneNumber'>
][];

const MANDATORY_COLUMNS = Object.keys(MANDATORY_COLUMN_TO_FIELD) as (keyof typeof MANDATORY_COLUMN_TO_FIELD)[];
const KNOWN_COLUMNS: ReadonlySet<string> = new Set([
  ...MANDATORY_COLUMNS,
  ...Object.keys(OPTIONAL_COLUMN_TO_FIELD),
  ...CONTACT_COLUMN_GROUPS.flatMap(([, columnToField]) => Object.keys(columnToField)),
]);

type ParsedReferralUploadRowContact = {
  respondentType: ContactRespondentType;
  givenName: string;
  familyName: string;
  phoneNumber: string;
};

type ParsedReferralUploadRow = {
  rowNumber: number;
  givenName: string;
  familyName: string;
  eprSystem: string;
  eprId: string;
  postalCode?: string;
  healthCareSystemId?: string;
  contacts?: ParsedReferralUploadRowContact[];
  careTeam?: string;
  patientPhoneNumber?: string;
};

type IncompleteRow = { rowNumber: number; columns: string };
type IncompleteContactRow = IncompleteRow & { respondentType: ContactRespondentType };
type DuplicateReferralRow = { rowNumber: number; eprId: string };
type TooLongRow = { rowNumber: number; columns: string };

type CsvParseError =
  | { key: 'malformedCsv'; params: { reason: string } }
  | { key: 'noDataRows' }
  | { key: 'tooManyRows'; params: { count: number; max: number } }
  | { key: 'emptyColumnHeader' }
  | { key: 'duplicateColumns'; params: { columns: string } }
  | { key: 'missingColumns'; params: { columns: string } }
  | { key: 'unrecognisedColumns'; params: { columns: string } }
  | { key: 'incompleteData'; rows: IncompleteRow[]; contactRows: IncompleteContactRow[] }
  | { key: 'fieldsTooLong'; rows: TooLongRow[]; max: number }
  | { key: 'duplicateReferrals'; rows: DuplicateReferralRow[] };

type ParsedReferralUploadCsv = { parseError?: CsvParseError; rows: ParsedReferralUploadRow[] };

function parseReferralUploadCsv(content: string): ParsedReferralUploadCsv {
  const headerPreview = Papa.parse<string[]>(content, { header: false, skipEmptyLines: true, preview: 1 });

  if (headerPreview.errors.length > 0) {
    return { parseError: { key: 'malformedCsv', params: { reason: headerPreview.errors[0].message } }, rows: [] };
  }

  const headerError = validateHeaders(headerPreview.data[0].map((header) => header.trim()));
  if (headerError) {
    return { parseError: headerError, rows: [] };
  }

  const { data, errors } = Papa.parse<Record<string, string>>(content, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.trim(),
  });

  if (errors.length > 0) {
    return { parseError: { key: 'malformedCsv', params: { reason: errors[0].message } }, rows: [] };
  }

  if (data.length === 0) {
    return { parseError: { key: 'noDataRows' }, rows: [] };
  }

  if (data.length > MAX_ROWS) {
    return { parseError: { key: 'tooManyRows', params: { count: data.length, max: MAX_ROWS } }, rows: [] };
  }

  return parseRows(data);
}

function parseRows(data: Record<string, string>[]): ParsedReferralUploadCsv {
  const rawRows = data.map((record, index) => ({ record, rowNumber: index + HEADER_ROW_NUMBER + 1 }));

  const contactRows = findIncompleteContactColumns(rawRows);
  const rows = rawRows.map(({ record, rowNumber }) => toParsedRow(record, rowNumber));
  const incompleteRows = findIncompleteRows(rows);

  if (incompleteRows.length > 0 || contactRows.length > 0) {
    return { parseError: { key: 'incompleteData', rows: incompleteRows, contactRows }, rows: [] };
  }

  const tooLongRows = findTooLongRows(rawRows);
  if (tooLongRows.length > 0) {
    return { parseError: { key: 'fieldsTooLong', rows: tooLongRows, max: MAX_FIELD_LENGTH }, rows: [] };
  }

  const duplicateRows = findDuplicateReferrals(rows);
  if (duplicateRows.length > 0) {
    return { parseError: { key: 'duplicateReferrals', rows: duplicateRows }, rows: [] };
  }

  return { rows };
}

function validateHeaders(headers: string[]): CsvParseError | undefined {
  if (headers.includes('')) {
    return { key: 'emptyColumnHeader' };
  }

  const duplicateColumns = findDuplicates(headers);
  if (duplicateColumns.length > 0) {
    return { key: 'duplicateColumns', params: { columns: duplicateColumns.join(', ') } };
  }

  const missingColumns = MANDATORY_COLUMNS.filter((column) => !headers.includes(column));
  if (missingColumns.length > 0) {
    return { key: 'missingColumns', params: { columns: missingColumns.join(', ') } };
  }

  const unrecognisedColumns = headers.filter((header) => !KNOWN_COLUMNS.has(header));
  if (unrecognisedColumns.length > 0) {
    return { key: 'unrecognisedColumns', params: { columns: unrecognisedColumns.join(', ') } };
  }

  return undefined;
}

function findDuplicates(headers: string[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();

  for (const header of headers) {
    if (seen.has(header)) duplicates.add(header);
    seen.add(header);
  }

  return [...duplicates];
}

function toParsedRow(record: Record<string, string>, rowNumber: number): ParsedReferralUploadRow {
  const row = { rowNumber } as ParsedReferralUploadRow;

  for (const [column, field] of Object.entries(MANDATORY_COLUMN_TO_FIELD)) {
    row[field] = record[column];
  }

  for (const [column, field] of Object.entries(OPTIONAL_COLUMN_TO_FIELD)) {
    const value = record[column];
    if (value !== undefined) {
      row[field] = value;
    }
  }

  const contacts = CONTACT_COLUMN_GROUPS.filter(([, columnToField]) =>
    Object.keys(columnToField).some((column) => record[column]?.trim())
  ).map(([respondentType, columnToField]) => {
    const contact = { respondentType } as ParsedReferralUploadRowContact;
    for (const [column, field] of Object.entries(columnToField)) {
      contact[field] = record[column];
    }
    return contact;
  });

  if (contacts.length > 0) {
    row.contacts = contacts;
  }

  return row;
}

function findIncompleteContactColumns(
  rawRows: { record: Record<string, string>; rowNumber: number }[]
): IncompleteContactRow[] {
  return rawRows
    .flatMap(({ record, rowNumber }) =>
      CONTACT_COLUMN_GROUPS.map(([respondentType, columnToField]) => {
        const values = Object.keys(columnToField).map((column) => ({
          column,
          value: record[column],
        }));

        const hasAny = values.some(({ value }) => value !== undefined && value.trim() !== '');
        if (!hasAny) {
          return undefined;
        }

        const missingColumns = values.filter(({ value }) => !value?.trim()).map(({ column }) => column);
        return missingColumns.length > 0
          ? { columns: missingColumns.join(', '), respondentType, rowNumber }
          : undefined;
      })
    )
    .filter((row): row is IncompleteContactRow => row !== undefined);
}

function findIncompleteRows(rows: ParsedReferralUploadRow[]): IncompleteRow[] {
  return rows
    .map((row) => {
      const missingColumns = Object.entries(MANDATORY_COLUMN_TO_FIELD)
        .filter(([, field]) => !row[field].trim())
        .map(([column]) => column);

      return missingColumns.length > 0 ? { columns: missingColumns.join(', '), rowNumber: row.rowNumber } : undefined;
    })
    .filter((row): row is IncompleteRow => row !== undefined);
}

function findTooLongRows(rawRows: { record: Record<string, string>; rowNumber: number }[]): TooLongRow[] {
  return rawRows
    .map(({ record, rowNumber }) => {
      const tooLongColumns = Object.entries(record)
        .filter(([column, value]) => KNOWN_COLUMNS.has(column) && value.trim().length > MAX_FIELD_LENGTH)
        .map(([column]) => column);

      return tooLongColumns.length > 0 ? { columns: tooLongColumns.join(', '), rowNumber } : undefined;
    })
    .filter((row): row is TooLongRow => row !== undefined);
}

function findDuplicateReferrals(rows: ParsedReferralUploadRow[]): DuplicateReferralRow[] {
  const seen = new Set<string>();
  const duplicates: DuplicateReferralRow[] = [];

  for (const { rowNumber, eprId } of rows) {
    const trimmedEprId = eprId.trim();

    if (seen.has(trimmedEprId)) {
      duplicates.push({ rowNumber, eprId: trimmedEprId });
    } else {
      seen.add(trimmedEprId);
    }
  }

  return duplicates;
}

export type { ContactRespondentType, CsvParseError, ParsedReferralUploadRow };
export { parseReferralUploadCsv };
